import type { DirectorSkillSelection } from '../chat/directorSkillSelection'
import type { DocumentAttachment } from '../shared/documentPrompt'

export type LandingUploadType = 'image' | 'video' | 'audio'

/**
 * Max number of IMAGE references a user may queue on the landing composer.
 * Mirrors the per-node reference picker cap (`maxReferences=4` in
 * ConfigTabContent). Only images are capped — video/audio uploads are
 * unbounded. Chosen for UX; the backend `MAX_REFERENCE_IMAGES=10` is a separate
 * per-generation ceiling applied downstream.
 */
export const MAX_LANDING_IMAGE_UPLOADS = 4

export type LandingUploadStatus = 'queued' | 'uploading' | 'uploaded' | 'error'

export interface LandingUploadReference {
  id: string
  type: LandingUploadType
  file: File
  name: string
  previewUrl: string
  status: LandingUploadStatus
  error?: string
}

export interface LandingUploadedReference {
  id: string
  type: LandingUploadType
  name: string
  path: string
  size: number
}

export interface LandingGenerateOptions {
  selectedSkill?: DirectorSkillSelection
  uploads?: LandingUploadReference[]
  referencedNodeIds?: string[]
  /**
   * Documents attached in the composer (RFC-1218). Text only — extracted at
   * attach time, so nothing here needs uploading after the project is created.
   * Folded into the first Director turn, NOT into the createProject payload,
   * whose `prompt` is capped at CREATIVE_TEXT_MAX_LENGTH (10 000) — the very
   * cap this feature exists to lift.
   */
  documents?: readonly DocumentAttachment[]
}

export function landingUploadTypeFromMime(mimeType: string): LandingUploadType | null {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  return null
}

export function createLandingUploadId(type: LandingUploadType): string {
  return `upload_${type}_${crypto.randomUUID()}`
}

export function outputFieldForLandingUpload(type: LandingUploadType): 'out_image' | 'out_video' | 'out_audio' {
  if (type === 'image') return 'out_image'
  if (type === 'video') return 'out_video'
  return 'out_audio'
}

export function labelForLandingUpload(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return 'Uploaded reference'
  return trimmed.replace(/\.[^.]+$/, '')
}

function fieldValue(value: object, key: string) {
  return Object.entries(value).find(([entryKey]) => entryKey === key)?.[1]
}

export function landingUploadedReferenceFromUnknown(value: object | null | undefined): LandingUploadedReference | null {
  if (!value) return null

  const id = fieldValue(value, 'id')
  const type = fieldValue(value, 'type')
  const name = fieldValue(value, 'name')
  const path = fieldValue(value, 'path')
  const size = fieldValue(value, 'size')
  if (typeof id !== 'string') return null
  if (type !== 'image' && type !== 'video' && type !== 'audio') return null
  if (typeof name !== 'string') return null
  if (typeof path !== 'string') return null

  return {
    id,
    type,
    name,
    path,
    size: typeof size === 'number' ? size : 0,
  }
}

export function landingUploadedReferencesFromUnknown(value: object | null | undefined): LandingUploadedReference[] {
  if (!Array.isArray(value)) return []
  const references: LandingUploadedReference[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const reference = landingUploadedReferenceFromUnknown(item)
    if (reference) references.push(reference)
  }
  return references
}

export function mergeLandingReferencedNodeIds(
  explicitIds: string[] | undefined,
  uploads: Array<Pick<LandingUploadReference, 'id'>>,
): string[] | undefined {
  const merged = new Set<string>()
  for (const id of explicitIds ?? []) {
    if (typeof id === 'string' && id.trim()) {
      merged.add(id)
    }
  }
  for (const upload of uploads) {
    if (upload.id.trim()) {
      merged.add(upload.id)
    }
  }
  return merged.size > 0 ? Array.from(merged) : undefined
}

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function removeLandingReferenceMarker(text: string, uploadId: string): string {
  if (!uploadId.trim()) return text
  const marker = new RegExp(`\\s*@\\[\\[${escapeRegexLiteral(uploadId)}\\]\\]`, 'g')
  return text
    .replace(marker, ' ')
    .replace(/[^\S\r\n]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .trim()
}
